import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import { GestorePageHeader } from '../../components/gestore/GestorePageHeader';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Building2, Plus, Pencil, Trash2, Users2, Grid3x3, KeyRound, Mail, Languages } from 'lucide-react';
import { LANGUAGES } from '../../i18n/languages';

export default function GestoreRestaurants() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const blank = { name: '', address: '', address_line2: '', phone: '', vat: '', active: true, language: 'it', license_key: '', admin_email: '' };
  const [f, setF] = useState(blank);

  const load = async () => {
    const { data } = await adminApi.get('/admin/restaurants');
    setItems(data);
  };
  const loadLicenses = async () => {
    const { data } = await adminApi.get('/admin/licenses');
    setLicenses(data);
  };
  useEffect(() => { load(); loadLicenses(); }, []);

  // Licenses selectable in the form: unassigned ones + the one currently bound to the edited restaurant
  const selectableLicenses = licenses.filter(
    (l) => !l.restaurant_id || (editing && l.key === editing.license_key)
  );

  const openCreate = () => { setEditing(null); setF(blank); setDialog(true); };
  const openEdit = (r) => {
    setEditing(r);
    setF({
      name: r.name || '', address: r.address || '', address_line2: r.address_line2 || '', phone: r.phone || '', vat: r.vat || '',
      active: r.active, language: r.language || 'it', license_key: r.license_key || '', admin_email: r.admin_email || '',
    });
    setDialog(true);
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error(t('gestore.rNameReq')); return; }
    if (!f.license_key) { toast.error(t('gestore.rSelectLicenseReq')); return; }
    if (!f.admin_email.trim()) { toast.error(t('gestore.rEmailReq')); return; }
    try {
      if (editing) await adminApi.put(`/admin/restaurants/${editing.id}`, f);
      else await adminApi.post('/admin/restaurants', f);
      toast.success(t('gestore.rBusinessSaved'));
      setDialog(false); setEditing(null); setF(blank);
      load(); loadLicenses();
    } catch (e) { toast.error(e?.response?.data?.detail || t('gestore.rError')); }
  };
  const del = async (r) => {
    setConfirmDel(null);
    try {
      await adminApi.delete(`/admin/restaurants/${r.id}`);
      load(); loadLicenses(); toast.success(t('gestore.rBusinessDeleted'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('gestore.rError')); }
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6">
      <GestorePageHeader title={t('gestore.rTitle')} subtitle={t('gestore.rSubtitle')}>
        <Button className="rounded-xl" data-testid="add-restaurant-button" onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> {t('gestore.rBusinessBtn')}</Button>
      </GestorePageHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="gestore-restaurants-grid">
        {items.map((r) => (
          <Card key={r.id} className="p-5" data-testid={`restaurant-card-${r.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Building2 className="h-5 w-5" /></div>
                <div><div className="font-semibold">{r.name}</div><div className="text-xs text-muted-foreground">{r.address || '—'}</div></div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${r.active ? 'bg-pos-free/10 text-pos-free' : 'bg-muted text-muted-foreground'}`}>{r.active ? t('gestore.rActive') : t('gestore.rSuspended')}</span>
            </div>
            <div className="mt-4 space-y-1.5 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <KeyRound className="h-4 w-4 shrink-0" />
                {r.license_key ? (
                  <span className="font-mono text-xs truncate">{r.license_key}</span>
                ) : (
                  <span className="text-destructive text-xs">{t('gestore.rNoLicense')}</span>
                )}
                {r.license_key && (
                  <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full ${r.license_enabled ? 'bg-pos-free/10 text-pos-free' : 'bg-destructive/10 text-destructive'}`}>
                    {r.license_enabled ? t('gestore.rEnabled') : t('gestore.rDisabled')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4 shrink-0" />
                <span className="text-xs truncate">{r.admin_email || '—'}</span>
              </div>
            </div>
            <div className="flex gap-4 mt-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Users2 className="h-4 w-4" /> {t('gestore.rUsersCount', { count: r.users_count })}</span>
              <span className="flex items-center gap-1"><Grid3x3 className="h-4 w-4" /> {t('gestore.rTablesCount', { count: r.tables_count })}</span>
            </div>
            <div className="flex gap-1 mt-4 pt-4 border-t border-border">
              <Button variant="ghost" size="sm" className="flex-1" data-testid={`edit-restaurant-${r.id}`} onClick={() => openEdit(r)}><Pencil className="h-4 w-4 mr-1" /> {t('gestore.rEdit')}</Button>
              <Button variant="ghost" size="sm" className="flex-1 text-destructive hover:text-destructive" data-testid={`delete-restaurant-${r.id}`} onClick={() => setConfirmDel(r)}><Trash2 className="h-4 w-4 mr-1" /> {t('gestore.rDelete')}</Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? t('gestore.rEditBusiness') : t('gestore.rNewBusiness')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t('gestore.rName')}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" data-testid="restaurant-name-input" /></div>
            <div><Label>{t('gestore.rAddress')}</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder={t('gestore.rAddressPlaceholder')} className="mt-1.5" data-testid="restaurant-address-input" /></div>
            <div><Label>{t('gestore.rAddressLine2')}</Label><Input value={f.address_line2} onChange={(e) => setF({ ...f, address_line2: e.target.value })} placeholder={t('gestore.rAddressLine2Placeholder')} className="mt-1.5" data-testid="restaurant-address2-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>{t('gestore.rPhone')}</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1.5" /></div>
              <div><Label>{t('gestore.rVat')}</Label><Input value={f.vat} onChange={(e) => setF({ ...f, vat: e.target.value })} className="mt-1.5" /></div>
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><Languages className="h-3.5 w-3.5" /> {t('gestore.rInterfaceLanguage')}</Label>
              <Select value={f.language} onValueChange={(v) => setF({ ...f, language: v })}>
                <SelectTrigger className="mt-1.5" data-testid="restaurant-language-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l.code} value={l.code}><span className="mr-2">{l.flag}</span>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="pt-2 border-t border-border">
              <Label className="flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> {t('gestore.rAssociatedLicense')}</Label>
              <Select value={f.license_key} onValueChange={(v) => setF({ ...f, license_key: v })}>
                <SelectTrigger className="mt-1.5" data-testid="restaurant-license-select"><SelectValue placeholder={t('gestore.rSelectLicense')} /></SelectTrigger>
                <SelectContent>
                  {selectableLicenses.length === 0 && <SelectItem value="__none" disabled>{t('gestore.rNoLicenseAvailable')}</SelectItem>}
                  {selectableLicenses.map((l) => (
                    <SelectItem key={l.key} value={l.key} className="font-mono text-xs">{l.key}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {t('gestore.rAccessEmail')}</Label>
              <Input type="email" value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} className="mt-1.5" placeholder="admin@ristorante.it" data-testid="restaurant-email-input" />
            </div>

            {!editing && (
              <div className="rounded-lg bg-muted/50 border border-border p-3 text-xs text-muted-foreground" data-testid="restaurant-default-cred-note">
                {t('gestore.rDefaultCredNote', { defaultValue: 'Credenziali predefinite: Password "admin123" · PIN "123456". L\'amministratore potrà cambiarle dalla pagina Utenti dopo il primo accesso.' })}
              </div>
            )}

            <label className="flex items-center gap-2 pt-1"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="restaurant-active-switch" /> {t('gestore.rActive')}</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>{t('gestore.rCancel')}</Button><Button onClick={save} data-testid="restaurant-save-button">{t('gestore.rSave')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('gestore.rDeleteTitle', { name: confirmDel?.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('gestore.rDeleteDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">{t('gestore.rCancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => del(confirmDel)} data-testid="confirm-ok" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{t('gestore.rDelete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
